import { Box, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactECharts from "echarts-for-react";
import { EChartsOption } from "echarts";
import useStyles from "../styles";
import LegendsCharts from "../../legends";
import maskUnits from "../../../utils/maskUnits";
import { MyLineDashConfig } from "./type";
import { formatDataLine } from "../../../functions/formatData";
import { SeriesFormat } from "../../../functions/formatData/types";
import { accumulatedSeries } from "../../../functions/formatData/acumulator";
import parseKeyToColomns from "../../../functions/formatData/parseKeytoColumn";
import { getColorsArray } from "../../../utils/getColorsPallet";
import calculateMetrics from "../utils/calculateMetrics";
import handleColors from "../utils/handleColors";

const mapCurveToECharts = (curve?: string) => {
  switch (curve) {
    case "basis":
    case "cardinal":
    case "catmullRom":
    case "monotoneX":
    case "monotoneY":
    case "natural":
      return { smooth: true, step: undefined };
    case "step":
    case "stepBefore":
      return { smooth: false, step: "start" };
    case "stepAfter":
      return { smooth: false, step: "end" };
    case "linear":
    default:
      return { smooth: false, step: undefined };
  }
};

const getYMinMax = (
  data: SeriesFormat[],
  factor = 1.1,
): { min: number; max: number } => {
  if (!data?.length) return { min: 0, max: 0 };

  const allValues = data.flatMap((series) =>
    series.data.map((point) => Number(point.y) || 0),
  );

  const cleanValues = allValues.filter(Number.isFinite);
  if (!cleanValues.length) return { min: 0, max: 0 };

  const hasNegativeValues = cleanValues.some((value) => value < 0);
  if (!hasNegativeValues) {
    return {
      min: 0,
      max: Math.max(...cleanValues) * factor,
    };
  }

  return {
    min: Math.min(...cleanValues) * factor,
    max: Math.max(...cleanValues) * factor,
  };
};

const LineChart: React.FC<MyLineDashConfig> = (props) => {
  const theme = useTheme();
  const styles = useStyles(theme);
  const [boxHeight, setBoxHeight] = useState<number | null>(null);
  const [boxWidth, setBoxWidth] = useState<number | null>(null);
  const lineRef = useRef<HTMLDivElement>(null);

  const {
    data,
    indexBy,
    units,
    fractionDigits,
    keys,
    groupBy,
    interval,
    viewMode,
    parseKeyToColumn,
    metricsType,
    curve,
    colors,
    colorsByKey,
    enableArea,
    enableGridX = false,
    enableGridY = true,
  } = props;

  useEffect(() => {
    const element = lineRef.current;
    if (!element) return;

    let resizeTimer: ReturnType<typeof setTimeout>;

    const resizeObserver = new ResizeObserver((entries) => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;
          const entry = entries[0];
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            setBoxWidth(width);
            setBoxHeight(height);
          }
        });
      }, 50);
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(resizeTimer);
    };
  }, []);

  const { formattedData, keyNames, formattedDataAccumulated } = useMemo(() => {
    if (!keys)
      return {
        formattedData: [],
        keyNames: [],
        formattedDataAccumulated: [],
      };
    const { parsedData, newKeys } = parseKeyToColomns(
      [...data],
      keys,
      indexBy,
      parseKeyToColumn,
    );
    const formatted = formatDataLine({
      data: parsedData as MyDatum[],
      indexBy,
      keys: newKeys,
      groupBy,
      interval,
    });
    const accumulated = accumulatedSeries({ data: formatted.formattedData });

    return {
      formattedData: formatted.formattedData,
      keyNames: formatted.keyNames,
      formattedDataAccumulated: accumulated,
    };
  }, [data, indexBy, keys, viewMode, interval, groupBy, parseKeyToColumn]);

  const activeData =
    viewMode === "accumulate" ? formattedDataAccumulated : formattedData;

  const chartOption: EChartsOption = useMemo(() => {
    let colorPalette: string[] = [];

    if (colorsByKey) {
      colorPalette = keyNames.map((key) =>
        handleColors(key, theme, colorsByKey),
      );
    } else if (colors) {
      colorPalette = getColorsArray(
        keyNames.length,
        colors,
        theme.palette.mode === "dark",
      );
    } else {
      colorPalette = getColorsArray(
        keyNames.length,
        "nivo",
        theme.palette.mode === "dark",
      );
    }

    const { min, max } = getYMinMax(activeData);
    const { smooth, step } = mapCurveToECharts(curve);
    const showPoint = (boxWidth || 1) / (activeData[0]?.data.length || 1) > 15;

    const series = activeData.map((seriesData, idx) => ({
      name: seriesData.id,
      type: "line",
      smooth: smooth,
      step: step as any,
      symbol: showPoint ? "circle" : "none",
      symbolSize: 8,
      areaStyle:
        enableArea || curve !== "linear"
          ? {
              opacity: curve !== "linear" ? 0.3 : 0.5,
            }
          : undefined,
      tooltip: {
        valueFormatter: (value: number) =>
          maskUnits(value, units, fractionDigits),
      },
      data: seriesData.data.map((point) => [point.x, point.y]),
    }));

    return {
      animation: true,
      color: colorPalette,
      grid: {
        top: 30,
        right: 24,
        bottom: 10,
        left: 10,
        containLabel: true,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        backgroundColor: theme.palette.background.default,
        textStyle: { color: theme.palette.text.primary },
        confine: true,
      },
      legend: { show: false },
      xAxis: {
        type: "category",
        boundaryGap: false,
        axisTick: { show: true },
        axisLine: { show: false },
        splitLine: {
          show: enableGridX,
          lineStyle: {
            type: "dashed",
            color: theme.palette.divider,
            opacity: 0.5,
          },
        },
        axisLabel: {
          fontSize: 10,
          color: theme.palette.text.secondary,
          hideOverlap: true,
        },
      },
      yAxis: {
        type: "value",
        min,
        max,
        splitLine: {
          show: enableGridY,
          lineStyle: {
            type: "dashed",
            color: theme.palette.divider,
            opacity: 0.5,
          },
        },
        axisLabel: {
          formatter: (value: number) => maskUnits(value, units, 0),
          fontSize: 10,
          color: theme.palette.text.secondary,
        },
      },
      series: series as any,
    };
  }, [
    activeData,
    keyNames,
    boxWidth,
    colors,
    colorsByKey,
    theme,
    units,
    fractionDigits,
    curve,
    enableArea,
    enableGridX,
    enableGridY,
  ]);

  return formattedData.length > 0 &&
    formattedData?.[0].data.length === 0 ? undefined : (
    <Box
      sx={{
        ...styles.boxContainer,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          ...styles.boxLegend,
          minHeight: metricsType ? "45px" : "25px",
          flexShrink: 0,
        }}
      >
        <LegendsCharts
          {...props}
          keys={[...(keyNames || [])]}
          metricsType={
            metricsType
              ? calculateMetrics(metricsType, data as any[])
              : undefined
          }
        />
      </Box>
      <Box
        sx={{
          ...styles.boxContainerSizeChart,
          overflow: "hidden",
          minWidth: 0,
          width: "100%",
          flexGrow: 1,
          height: "100%",
          position: "relative",
        }}
        ref={lineRef}
      >
        {/* Always render layout to force expansion */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <ReactECharts
            option={chartOption}
            style={{ height: "100%", width: "100%" }}
            notMerge={true}
            lazyUpdate={true}
            autoResize={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default LineChart;
